// src/pages/PainelAgendamentos/FormEditarAgendamento.jsx
import { X, Pencil } from "lucide-react";
import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import CamposAgendamento from "./CamposAgendamento";
import CamposTecnico from "./CamposTecnico";
import CampoRelatoVisita from "./CampoRelatoVisita";
import MenuAgendamento from "./MenuAgendamento";

const API_URL = process.env.REACT_APP_API_URL;

// 🔹 Função auxiliar para garantir formato YYYY-MM-DD
function formatarData(dataStr) {
  if (!dataStr) return "";
  if (dataStr === "0000-00-00") return "";

  // caso venha "2025-09-16 14:30:00"
  if (dataStr.includes(" ")) {
    return dataStr.split(" ")[0];
  }

  // caso venha "16/09/2025"
  if (dataStr.includes("/")) {
    const [dia, mes, ano] = dataStr.split("/");
    return `${ano}-${mes}-${dia}`;
  }

  return dataStr; // já está no formato certo
}

export default function FormEditarAgendamento({
  agendamento,
  onClose,
  onAtualizar,
}) {
  const [data, setData] = useState(agendamento?.data_abertura || "");
  const [hora, setHora] = useState(agendamento?.hora_abertura || "");
  const [empresa, setEmpresa] = useState(agendamento?.empresa || "");
  const [clientes, setClientes] = useState([]);

  const [colaborador, setColaborador] = useState(
    agendamento?.colaborador || ""
  );
  const [tecnico, setTecnico] = useState(
    agendamento?.tecnico_responsavel || ""
  );
  const [tipoServico, setTipoServico] = useState(
    agendamento?.tipo_servico || ""
  );
  const [observacao, setObservacao] = useState(agendamento?.observacao || "");
  const [relato, setRelato] = useState(agendamento?.relato || "");
  const [status, setStatus] = useState(agendamento?.status || "Pendente");

  // ✅ agora já vem formatado para o input[type="date"]
  const [dataFinalizado, setDataFinalizado] = useState(
    formatarData(agendamento?.finalizado_em)
  );

  // 🔹 Buscar clientes
  useEffect(() => {
    fetch(`${API_URL}/cliente/listar.php`)
      .then((r) =>
        r.headers.get("content-type")?.includes("json") ? r.json() : null
      )
      .then((json) => {
        if (
          json?.dados &&
          (json.status === "success" || json.status === "sucesso")
        ) {
          setClientes(json.dados);
        }
      })
      .catch(() => setClientes([]));
  }, []);

  // 🔹 Função para salvar alterações (UPDATE)
  const handleSalvar = async (novoStatus = status) => {
    try {
      const response = await fetch(`${API_URL}/agendamento/editar.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: agendamento.id,
          data,
          hora,
          empresa,
          responsavelEmpresa: colaborador,
          tecnico,
          servico: tipoServico,
          observacao,
          relato,
          status: novoStatus,
          dataFinalizado: dataFinalizado || null, // ✅ se estiver vazio vai null pro banco
          linkAnexado: null,
        }),
      });

      const result = await response.json();

      if (result?.status === true) {
        toast.success("✅ Agendamento atualizado com sucesso!");
        if (onAtualizar) onAtualizar(); // ✅ atualiza tabela em tempo real
        onClose();
      } else {
        toast.error(result?.mensagem || "❌ Erro ao atualizar agendamento!");
        console.error("Erro:", result);
      }
    } catch (err) {
      console.error("Erro de conexão:", err);
      toast.error("❌ Não foi possível conectar ao servidor!");
    }
  };

  // 🔹 Cancelar edição
  const handleCancelar = () => {
    toast("🚫 Edição cancelada", { icon: "⚠️" });
    onClose();
  };

  // 🔹 Finalizar direto (salva no banco também)
  const handleFinalizar = () => {
    setStatus("Concluído");
    setDataFinalizado(new Date().toISOString().split("T")[0]); // ✅ seta data final no formato correto
    handleSalvar("Concluído");
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-md shadow-md border border-gray-300 overflow-hidden">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#2b2b2b]">
        <div className="flex items-center gap-2">
          <Pencil size={20} className="text-white" />
          <h2 className="text-lg font-semibold text-white">
            Editando Agendamento
          </h2>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-black 
                   transition hover:scale-110 hover:bg-slate-200"
        >
          <X size={16} />
        </button>
      </div>

      {/* Conteúdo */}
      <div className="flex flex-1 overflow-hidden">
        {/* Formulário */}
        <div className="flex-1 p-6 overflow-y-auto">
          <CamposAgendamento
            data={data}
            setData={setData}
            hora={hora}
            setHora={setHora}
            dataFinalizado={dataFinalizado}   // ✅ já formatado
            setDataFinalizado={setDataFinalizado}
            empresa={empresa}
            setEmpresa={setEmpresa}
            clientes={clientes}
          />

          {/* Botão para limpar data finalizado */}
          {dataFinalizado && (
            <button
              type="button"
              onClick={() => setDataFinalizado("")}
              className="mt-2 px-3 py-1 text-xs bg-red-100 text-red-600 rounded hover:bg-red-200"
            >
              Apagar Data de Finalização
            </button>
          )}

          <CamposTecnico
            colaborador={colaborador}
            setColaborador={setColaborador}
            tecnico={tecnico}
            setTecnico={setTecnico}
            tipoServico={tipoServico}
            setTipoServico={setTipoServico}
          />
          <div className="mt-6">
            <CampoRelatoVisita
              observacoes={observacao}
              setObservacoes={setObservacao}
              relatoVisita={relato}
              setRelatoVisita={setRelato}
            />
          </div>
        </div>

        {/* Menu lateral */}
        <div className="w-40 border-l border-slate-300 bg-gray-50 flex flex-col p-4">
          <MenuAgendamento
            onSalvar={() => handleSalvar()}
            onCancelar={handleCancelar}
            onFinalizar={handleFinalizar}
            status={status}
            setStatus={setStatus}
          />
        </div>
      </div>
    </div>
  );
}
